import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The type-scale steps declared as `--text-*` in src/theme/tokens.css.
 *
 * tailwind-merge has to be TOLD about these. Its `font-size` class group is a
 * fixed list of Tailwind's stock names (xs…9xl plus arbitrary values), so a
 * custom `text-label` falls through to the `text-color` group instead — and
 * then `cn('… text-label', selected ? 'text-chip-on-foreground' : 'text-text')`
 * reads as two colours in conflict and DROPS the size silently.
 *
 * That is not hypothetical: it is what shipped for one build. The chips came
 * out at the inherited 14px/450 instead of 12.5px/550 with no error anywhere,
 * because a merge that removes a class produces valid CSS for a class that is
 * simply gone. Every component here composes through `cn`, so the failure was
 * invisible in the source and only showed in a computed-style probe.
 *
 * Keep this list in sync with the `--text-*` tokens. A step that is missing
 * here still works until the day someone adds a text colour beside it.
 */
const TYPE_SCALE = [
  'display',
  'lead',
  'title',
  'heading',
  'price',
  'body',
  'label',
  'caption',
  'micro',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: TYPE_SCALE }],
      // Same trap, one namespace over: `font-book` / `font-mid` / `font-strong`
      // are the interpolated weights off Geist's variable axis, and without
      // this they would be parsed as font-FAMILY names and never conflict with
      // (or be overridden by) `font-bold` the way a weight must.
      'font-weight': [{ font: ['book', 'mid', 'strong'] }],
    },
  },
});

/**
 * shadcn-style class combiner. Lives inside the design agent's owned path;
 * the integration lead may re-export it from src/lib/utils.ts if later
 * features expect the conventional location.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
